import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Modal } from "antd";
import Select from "react-select";
import * as Md from "react-icons/md";
import * as Fi from "react-icons/fi";
import Utils from "../../utils/Utils";
import EnquiriesTableSkelton from "./EnquiriesTableSkelton";
import {
  getStatusColour,
  isReservationExpired,
} from "../../constants/ReservationStatus";
import TablePagination, {
  DEFAULT_PAGE_SIZE,
  paginateList,
} from "./TablePagination";
import { AppContext } from "../../context/AppContext";

function splitBooking(bookingTime) {
  if (!bookingTime) return { date: null, time: null };
  const [date, time] = String(bookingTime).split(" ");
  return { date, time };
}

function formatBookingDate(dateStr) {
  if (!dateStr) return "N/A";
  const d = new Date(
    /^\d{4}-\d{2}-\d{2}$/.test(dateStr) ? `${dateStr}T00:00:00` : dateStr
  );
  if (Number.isNaN(d.getTime())) return Utils.formatDate(dateStr) || "N/A";
  return d.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function parseTableIds(tables) {
  if (!tables) return [];
  let parsed = tables;
  if (typeof tables === "string") {
    try {
      parsed = JSON.parse(tables);
    } catch {
      return [];
    }
  }
  return Array.isArray(parsed)
    ? parsed.filter((id) => id != null && id !== "")
    : [];
}

function getTableLabel(enquiry, tableLists) {
  if (
    Array.isArray(enquiry?.tablesDetails) &&
    enquiry.tablesDetails.length > 0
  ) {
    const names = enquiry.tablesDetails
      .filter(Boolean)
      .map(
        (t) =>
          t?.name || t?.tableName || (t?.id != null ? `Table ${t.id}` : null)
      )
      .filter(Boolean);
    if (names.length > 0) return names.join(", ");
  }

  const ids = parseTableIds(enquiry?.tables);
  if (ids.length === 0) return "Unassigned";

  const labels = ids.map((id) => {
    const info =
      Array.isArray(tableLists) &&
      tableLists.find((t) => String(t.id) === String(id));
    return info?.name || info?.tableName || `Table ${id}`;
  });

  return labels.join(", ") || "Unassigned";
}

function getPrimaryAction(status) {
  if (status === "Requested" || status === "Modified") {
    return {
      label: "Approve",
      nextStatus: "Approved",
      tone: "approve",
      icon: "check",
    };
  }
  if (status === "Approved") {
    return {
      label: "Seat guests",
      nextStatus: "Seated",
      tone: "primary",
      icon: "table",
    };
  }
  if (status === "Seated") {
    return {
      label: "Mark completed",
      nextStatus: "Completed",
      tone: "primary",
      icon: "check",
      full: true,
    };
  }
  return null;
}

function PrimaryActionIcon({ type }) {
  if (type === "table") {
    return <Md.MdOutlineTableBar aria-hidden />;
  }
  if (type === "check") {
    return <Fi.FiCheck aria-hidden />;
  }
  return null;
}

function hasAssignedTables(enquiry) {
  if (
    Array.isArray(enquiry?.tablesDetails) &&
    enquiry.tablesDetails.some(Boolean)
  ) {
    return true;
  }
  return parseTableIds(enquiry?.tables).length > 0;
}

function getBookingDateTime(enquiry) {
  const raw = enquiry?.bookingTime || "";
  if (typeof raw === "string" && raw.includes(" ")) return raw.trim();
  if (enquiry?.bookingDate && enquiry?.bookingTime) {
    return `${enquiry.bookingDate} ${enquiry.bookingTime}`;
  }
  return raw;
}

function buildStatusPayload(enquiry, nextStatus, tableIdsOverride = null) {
  const {
    id: _id,
    formattedID: _formattedID,
    status: _status,
    addedTime: _addedTime,
    tablesDetails: _tablesDetails,
    currencyDetails: _currencyDetails,
    colour: _colour,
    textColour: _textColour,
    bookingTime: _bookingTime,
    bookingDate: _bookingDate,
    ...rest
  } = enquiry || {};

  const tableIds =
    tableIdsOverride != null
      ? tableIdsOverride
      : parseTableIds(enquiry?.tables);

  // Match ViewReservation: send bookingDate + bookingTime separately
  let bookingDate = enquiry?.bookingDate || "";
  let bookingTime = enquiry?.bookingTime || "";
  if (typeof bookingTime === "string" && bookingTime.includes(" ")) {
    const [datePart, timePart] = bookingTime.split(" ");
    bookingDate = datePart || bookingDate;
    bookingTime = timePart || "";
  }

  return {
    ...rest,
    tables: tableIds,
    bookingDate: bookingDate || "",
    bookingTime: bookingTime || "",
    status: nextStatus,
    advancePayment: enquiry?.advancePayment ?? "",
    advanceAmount: enquiry?.advanceAmount ?? "",
    amountStatus: enquiry?.amountStatus ?? "",
    shopMessage: enquiry?.shopMessage ?? enquiry?.shopmessage ?? "",
    paymentMethod: enquiry?.paymentMethod ?? "",
    source: "REACT",
  };
}

function EnquiryListPanel({
  items,
  loading = false,
  title = "Reservations",
  emptyMessage = "No reservations",
  showStatus = true,
  pageSize = DEFAULT_PAGE_SIZE,
}) {
  const navigate = useNavigate();
  const {
    tableLists,
    updateEnquiry,
    enquiryActionLoading,
    tokenData,
    fetchNewReservations,
    todaysReservationList,
    fetchFutureReservations,
    upcomingwithtodaysReservationList,
    fetchAvailableTables,
  } = useContext(AppContext);
  const [page, setPage] = useState(1);
  const [actionId, setActionId] = useState(null);
  const [seatModalEnquiry, setSeatModalEnquiry] = useState(null);
  const [availTables, setAvailTables] = useState([]);
  const [selectedTables, setSelectedTables] = useState(null);
  const [tablesLoading, setTablesLoading] = useState(false);
  const list = Array.isArray(items) ? items : [];

  useEffect(() => {
    setPage(1);
  }, [items]);

  const {
    items: pageItems,
    page: safePage,
    total,
  } = paginateList(list, page, pageSize);

  const openReservation = (enquiry, e) => {
    e?.stopPropagation?.();
    if (!enquiry?.id) return;
    navigate(`/view-reservation/${enquiry.id}`);
  };

  const refreshLists = async () => {
    const shopToken =
      tokenData?.shopCustomToken ||
      window.localStorage.getItem("shopCustomToken");
    if (!shopToken) return;
    await fetchNewReservations(shopToken);
    await todaysReservationList(shopToken);
    await fetchFutureReservations(shopToken);
    await upcomingwithtodaysReservationList?.();
  };

  const closeSeatModal = () => {
    setSeatModalEnquiry(null);
    setAvailTables([]);
    setSelectedTables(null);
    setTablesLoading(false);
  };

  const openSeatTableModal = async (enquiry) => {
    setSeatModalEnquiry(enquiry);
    setSelectedTables(null);
    setAvailTables([]);

    const bookingDateTime = getBookingDateTime(enquiry);
    if (!bookingDateTime) {
      toast.error("Booking time is missing for this reservation.");
      return;
    }

    try {
      setTablesLoading(true);
      await fetchAvailableTables(
        { bookingTime: bookingDateTime },
        {
          onSuccess: (res) => {
            const available = res?.data?.available ?? [];
            if (available.length === 0) {
              toast.info("No tables available for this time slot.");
            }
            setAvailTables(
              available.map((d) => ({
                value: d?.id,
                label: `${d?.name || "Table"}${
                  d?.chairs ? `  (${d.chairs})` : ""
                }`,
                meta: d,
              }))
            );
          },
          onFailed: (err) => {
            console.error("Error fetching available tables", err);
            toast.error("Could not load available tables.");
            setAvailTables([]);
          },
        }
      );
    } finally {
      setTablesLoading(false);
    }
  };

  const submitStatusUpdate = async (enquiry, nextStatus, tableIds = null) => {
    const shopToken =
      tokenData?.shopCustomToken ||
      window.localStorage.getItem("shopCustomToken");
    if (!shopToken) {
      toast.error("Authorisation token missing.");
      return;
    }

    const payload = buildStatusPayload(enquiry, nextStatus, tableIds);
    setActionId(enquiry.id);

    try {
      await updateEnquiry(enquiry.id, payload, {
        onSuccess: async () => {
          toast.success(
            nextStatus === "Approved"
              ? "Reservation approved!"
              : nextStatus === "Seated"
                ? "Guests seated!"
                : nextStatus === "Completed"
                  ? "Reservation marked completed!"
                  : "Reservation updated!"
          );
          closeSeatModal();
          await refreshLists();
        },
        onFailed: (err) => {
          toast.error(err?.messages ?? "Update failed. Please try again.");
        },
        headers: { "x-shopToken": shopToken },
      });
    } finally {
      setActionId(null);
    }
  };

  const handleStatusAction = async (enquiry, nextStatus, e) => {
    e?.stopPropagation?.();
    if (!enquiry?.id || !nextStatus) return;

    if (nextStatus === "Seated" && !hasAssignedTables(enquiry)) {
      await openSeatTableModal(enquiry);
      return;
    }

    await submitStatusUpdate(enquiry, nextStatus);
  };

  const confirmSeatWithTables = async () => {
    if (!seatModalEnquiry) return;

    if (!selectedTables || selectedTables.length === 0) {
      toast.error("Please select at least one table before seating guests.");
      return;
    }

    const partySize = Number(seatModalEnquiry?.chairs) || 0;
    const totalChairs = selectedTables.reduce(
      (sum, t) => sum + (Number(t?.meta?.chairs) || 0),
      0
    );
    if (partySize > 0 && totalChairs > 0 && totalChairs < partySize) {
      toast.error(
        `Selected tables seat ${totalChairs}, but party size is ${partySize}.`
      );
      return;
    }

    const tableIds = [
      ...new Set(
        selectedTables.map((table) => Number(table?.value)).filter(Boolean)
      ),
    ];

    await submitStatusUpdate(seatModalEnquiry, "Seated", tableIds);
  };

  return (
    <div className="el-panel">
      {/* Desktop table */}
      <div className="el-table-wrap">
        <table className="el-table">
          <thead>
            <tr>
              <th>Guest</th>
              <th>Reservation ID</th>
              <th>Booking date</th>
              <th>Time</th>
              <th>Party size</th>
              {showStatus && <th>Status</th>}
              <th className="el-table__actions-col">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <EnquiriesTableSkelton cols={showStatus ? 7 : 6} />
            ) : list.length === 0 ? (
              <tr>
                <td colSpan={showStatus ? 7 : 6} className="el-table__empty">
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              pageItems.map((enquiry) => {
                const { date, time } = splitBooking(enquiry?.bookingTime);
                const id = enquiry?.id ?? enquiry?.formattedID;
                const statusColour = getStatusColour(enquiry?.status);
                const expired = isReservationExpired(enquiry);
                const primaryAction = getPrimaryAction(enquiry?.status);
                const isBusy =
                  actionId === enquiry?.id ||
                  (enquiryActionLoading && actionId === enquiry?.id);
                const canAct = primaryAction && !expired;

                return (
                  <tr
                    key={id}
                    className="el-table__row is-clickable"
                    onClick={() => openReservation(enquiry)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        openReservation(enquiry);
                      }
                    }}
                    tabIndex={0}
                    role="link"
                    aria-label={`View reservation for ${enquiry?.name ?? "guest"}`}
                  >
                    <td className="el-table__guest">
                      <span className="el-guest-name">
                        {enquiry?.name ?? "N/A"}
                      </span>
                    </td>
                    <td className="el-table__id">
                      {enquiry?.formattedID ?? "N/A"}
                    </td>
                    <td>{formatBookingDate(date)}</td>
                    <td>
                      {time != null ? Utils.convertTiming(time) : "N/A"}
                    </td>
                    <td>
                      <span className="el-party">
                        <Fi.FiUser aria-hidden />
                        {enquiry?.chairs ?? "N/A"}
                      </span>
                    </td>
                    {showStatus && (
                      <td>
                        <span
                          className="el-status"
                          style={{
                            backgroundColor: statusColour.text,
                            color: statusColour.bg,
                            borderColor: statusColour.text,
                          }}
                        >
                          {enquiry?.status ?? "N/A"}
                        </span>
                      </td>
                    )}
                    <td className="el-table__actions-col">
                      {canAct ? (
                        <button
                          type="button"
                          className={`el-card__btn el-card__btn--${primaryAction.tone} el-table__action-btn`}
                          disabled={isBusy}
                          onClick={(e) =>
                            handleStatusAction(
                              enquiry,
                              primaryAction.nextStatus,
                              e
                            )
                          }
                        >
                          {isBusy ? "Please wait…" : primaryAction.label}
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="el-card__btn el-card__btn--ghost el-table__action-btn"
                          onClick={(e) => openReservation(enquiry, e)}
                        >
                          View booking
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="el-cards" aria-label={title}>
        {loading ? (
          <div className="el-cards__loading">Loading…</div>
        ) : list.length === 0 ? (
          <div className="el-cards__empty">{emptyMessage}</div>
        ) : (
          pageItems.map((enquiry) => {
            const { time } = splitBooking(enquiry?.bookingTime);
            const id = enquiry?.id ?? enquiry?.formattedID;
            const statusColour = getStatusColour(enquiry?.status);
            const expired = isReservationExpired(enquiry);
            const tableLabel = getTableLabel(enquiry, tableLists);
            const primaryAction = getPrimaryAction(enquiry?.status);
            const isBusy =
              actionId === enquiry?.id ||
              (enquiryActionLoading && actionId === enquiry?.id);
            const showDualActions =
              primaryAction && !primaryAction.full && !expired;
            const showCompletedOnly =
              primaryAction?.full && !expired;

            return (
              <article key={id} className="el-card">
                <div className="el-card__body">
                  <div className="el-card__left">
                    <div className="el-card__time">
                      <Md.MdOutlineAccessTime aria-hidden />
                      {time != null ? Utils.convertTiming(time) : "N/A"}
                    </div>
                    <div className="el-card__name">
                      <span className="el-guest-name">
                        {enquiry?.name ?? "N/A"}
                        {/* {expired && (
                          <span className="el-expired-badge">Expired</span>
                        )} */}
                      </span>
                    </div>
                    <div className="el-card__meta">
                      <span className="el-card__meta-item">
                        <Fi.FiUser aria-hidden />
                        {enquiry?.chairs ?? "N/A"}
                      </span>
                      <span className="el-card__sep" aria-hidden>
                        |
                      </span>
                      <span className="el-card__meta-item">
                        <Md.MdOutlineTableBar aria-hidden />
                        {tableLabel}
                      </span>
                    </div>
                  </div>

                  <div className="el-card__right">
                    {showStatus && enquiry?.status && (
                      <span
                        className="el-status el-status--card"
                        style={{
                          backgroundColor: statusColour.text,
                          color: statusColour.bg,
                          borderColor: statusColour.text,
                        }}
                      >
                        {enquiry.status}
                      </span>
                    )}
                    <button
                      type="button"
                      className="el-view-btn"
                      aria-label="View reservation"
                      onClick={(e) => openReservation(enquiry, e)}
                    >
                      <Fi.FiChevronRight />
                    </button>
                  </div>
                </div>

                <div className="el-card__actions">
                  {showCompletedOnly ? (
                    <button
                      type="button"
                      className={`el-card__btn el-card__btn--${primaryAction.tone} el-card__btn--full`}
                      disabled={isBusy}
                      onClick={(e) =>
                        handleStatusAction(
                          enquiry,
                          primaryAction.nextStatus,
                          e
                        )
                      }
                    >
                      {isBusy ? (
                        "Please wait…"
                      ) : (
                        <>
                          <PrimaryActionIcon type={primaryAction.icon} />
                          {primaryAction.label}
                        </>
                      )}
                    </button>
                  ) : showDualActions ? (
                    <>
                      <button
                        type="button"
                        className="el-card__btn el-card__btn--ghost"
                        onClick={(e) => openReservation(enquiry, e)}
                      >
                        View booking
                      </button>
                      <button
                        type="button"
                        className={`el-card__btn el-card__btn--${primaryAction.tone}`}
                        disabled={isBusy}
                        onClick={(e) =>
                          handleStatusAction(
                            enquiry,
                            primaryAction.nextStatus,
                            e
                          )
                        }
                      >
                        {isBusy ? (
                          "Please wait…"
                        ) : (
                          <>
                            <PrimaryActionIcon type={primaryAction.icon} />
                            {primaryAction.label}
                          </>
                        )}
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      className="el-card__btn el-card__btn--ghost el-card__btn--full"
                      onClick={(e) => openReservation(enquiry, e)}
                    >
                      View booking
                    </button>
                  )}
                </div>
              </article>
            );
          })
        )}
      </div>

      {!loading && total > 0 && (
        <TablePagination
          page={safePage}
          pageSize={pageSize}
          total={total}
          onPageChange={setPage}
        />
      )}

      <Modal
        open={!!seatModalEnquiry}
        onCancel={closeSeatModal}
        footer={null}
        title="Select table to seat guests"
        destroyOnClose
        centered
      >
        <div className="el-seat-modal">
          <p className="el-seat-modal__text">
            This booking is unassigned. Choose table(s) before seating{" "}
            <strong>{seatModalEnquiry?.name || "the guest"}</strong>
            {seatModalEnquiry?.chairs
              ? ` (${seatModalEnquiry.chairs} guests)`
              : ""}
            .
          </p>
          <label className="form-label" htmlFor="el-seat-tables">
            Available tables
          </label>
          <Select
            inputId="el-seat-tables"
            options={availTables}
            isMulti
            isLoading={tablesLoading}
            isDisabled={tablesLoading || enquiryActionLoading}
            value={selectedTables}
            onChange={(value) => setSelectedTables(value)}
            placeholder={
              tablesLoading
                ? "Loading tables…"
                : availTables.length === 0
                  ? "No tables available for this slot"
                  : "Choose table(s)"
            }
            noOptionsMessage={() => "No tables available for this slot"}
            menuPortalTarget={
              typeof document !== "undefined" ? document.body : null
            }
            menuPosition="fixed"
            styles={{
              control: (base) => ({
                ...base,
                minHeight: 42,
                borderRadius: 10,
                borderColor: "#e5e7eb",
                boxShadow: "none",
              }),
              menuPortal: (base) => ({
                ...base,
                zIndex: 1100,
              }),
            }}
          />
          <div className="el-seat-modal__actions">
            <button
              type="button"
              className="el-card__btn el-card__btn--ghost"
              onClick={closeSeatModal}
              disabled={enquiryActionLoading}
            >
              Cancel
            </button>
            <button
              type="button"
              className="el-card__btn el-card__btn--primary"
              onClick={confirmSeatWithTables}
              disabled={
                tablesLoading ||
                enquiryActionLoading ||
                actionId === seatModalEnquiry?.id
              }
            >
              {actionId === seatModalEnquiry?.id
                ? "Please wait…"
                : "Seat guests"}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default EnquiryListPanel;
